import { lazy, Suspense } from "react";
import { LoaderCircle } from "lucide-react";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { useAuth } from "./auth/AuthProvider";
import { RecoverableAuthError } from "./components/RecoverableAuthError";
import { AppShell } from "./layout/AppShell";
import type { Role } from "./types/crm";
const LoginPage = lazy(() => import("./pages/LoginPage").then((module) => ({ default: module.LoginPage })));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage").then((module) => ({ default: module.ForgotPasswordPage })));
const AuthCallbackPage = lazy(() => import("./pages/AuthCallbackPage").then((module) => ({ default: module.AuthCallbackPage })));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage").then((module) => ({ default: module.ResetPasswordPage })));
const DashboardPage = lazy(() => import("./pages/DashboardPage").then((module) => ({ default: module.DashboardPage })));
const CustomersPage = lazy(() => import("./pages/CustomersPage").then((module) => ({ default: module.CustomersPage })));
const LeadDevelopmentPage = lazy(() => import("./pages/LeadDevelopmentPage").then((module) => ({ default: module.LeadDevelopmentPage })));
const CustomerDetailPage = lazy(() => import("./pages/CustomerDetailPage").then((module) => ({ default: module.CustomerDetailPage })));
const ProductsPage = lazy(() => import("./pages/ProductsPage").then((module) => ({ default: module.ProductsPage })));
const ProductDetailPage = lazy(() => import("./pages/ProductDetailPage").then((module) => ({ default: module.ProductDetailPage })));
const TasksPage = lazy(() => import("./pages/TasksPage").then((module) => ({ default: module.TasksPage })));
const SamplesPage = lazy(() => import("./pages/SamplesPage").then((module) => ({ default: module.SamplesPage })));
const SampleDetailPage = lazy(() => import("./pages/SampleDetailPage").then((module) => ({ default: module.SampleDetailPage })));
const OpportunitiesPage = lazy(() => import("./pages/OpportunitiesPage").then((module) => ({ default: module.OpportunitiesPage })));
const WhatsAppPage = lazy(() => import("./pages/WhatsAppPage").then((module) => ({ default: module.WhatsAppPage })));
const EmailPage = lazy(() => import("./pages/EmailPage").then((module) => ({ default: module.EmailPage })));
const ReportsPage = lazy(() => import("./pages/ReportsPage").then((module) => ({ default: module.ReportsPage })));
const ImportPage = lazy(() => import("./pages/ImportPage").then((module) => ({ default: module.ImportPage })));
const UsagePage = lazy(() => import("./pages/UsagePage").then((module) => ({ default: module.UsagePage })));
const UsersPage = lazy(() => import("./pages/UsersPage").then((module) => ({ default: module.UsersPage })));
const AuditPage = lazy(() => import("./pages/AuditPage").then((module) => ({ default: module.AuditPage })));
const SettingsPage = lazy(() => import("./pages/SettingsPage").then((module) => ({ default: module.SettingsPage })));

function RouteLoader() {
  return <div className="flex min-h-[50vh] items-center justify-center"><LoaderCircle className="size-7 animate-spin text-blue-600" /></div>;
}

function ProtectedLayout() {
  const { profile, loading, error, retryProfile, signOut } = useAuth();
  const location = useLocation();
  if (loading) return <div className="flex min-h-screen items-center justify-center bg-slate-50"><div className="text-center"><LoaderCircle className="mx-auto size-8 animate-spin text-blue-600" /><p className="mt-3 text-sm text-slate-500">正在验证登录与数据权限…</p></div></div>;
  if (error) return <RecoverableAuthError error={error} retry={retryProfile} signOut={signOut} />;
  if (!profile) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return <AppShell><Outlet /></AppShell>;
}

function RequireRole({ roles }: { roles: Role[] }) {
  const { profile } = useAuth();
  return profile && roles.includes(profile.role) ? <Outlet /> : <Navigate to="/" replace />;
}

function NotFoundPage() {
  return <div className="flex min-h-[60vh] flex-col items-center justify-center text-center"><div className="text-7xl font-semibold tracking-tight text-slate-200">404</div><h1 className="mt-4 text-xl font-semibold text-slate-900">页面不存在</h1><p className="mt-2 text-sm text-slate-500">地址可能已变化，或当前账号没有此页面的权限。</p></div>;
}

export default function App() {
  return <Suspense fallback={<RouteLoader />}><Routes>
    <Route path="/login" element={<LoginPage />} />
    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
    <Route path="/auth/callback" element={<AuthCallbackPage />} />
    <Route path="/reset-password" element={<ResetPasswordPage />} />
    <Route element={<ProtectedLayout />}>
      <Route index element={<DashboardPage />} />
      <Route path="customers" element={<CustomersPage />} />
      <Route path="leads" element={<LeadDevelopmentPage />} />
      <Route path="customers/:id" element={<CustomerDetailPage />} />
      <Route path="products" element={<ProductsPage />} />
      <Route path="products/:id" element={<ProductDetailPage />} />
      <Route path="tasks" element={<TasksPage />} />
      <Route path="key-customers" element={<CustomersPage keyOnly />} />
      <Route path="samples" element={<SamplesPage />} />
      <Route path="samples/:id" element={<SampleDetailPage />} />
      <Route path="opportunities" element={<OpportunitiesPage />} />
      <Route path="whatsapp" element={<WhatsAppPage />} />
      <Route path="email" element={<EmailPage />} />
      <Route path="reports" element={<ReportsPage />} />
      <Route path="usage" element={<UsagePage />} />
      <Route element={<RequireRole roles={["super_admin", "manager", "salesperson"]} />}><Route path="imports" element={<ImportPage />} /></Route>
      <Route element={<RequireRole roles={["super_admin", "manager"]} />}><Route path="users" element={<UsersPage />} /><Route path="audit" element={<AuditPage />} /></Route>
      <Route element={<RequireRole roles={["super_admin"]} />}><Route path="settings" element={<SettingsPage />} /></Route>
      <Route path="*" element={<NotFoundPage />} />
    </Route>
  </Routes></Suspense>;
}
